import React, { useState } from "react";
import PropTypes from "prop-types";
import LoadingIndicator from "../loading-indicator";
import { getPdf, fetchPdfs } from "../../services/policyholderService";
import { Translations } from "../translations";
import DocumentService from "../../services/documentService";
import InfoModal from "../modals/InfoModal";
import { Button } from "@ufginsurance/ui-kit";
import { LOADING_DOCUMENT_ROUTE } from "../../constants/routes";
import { AnalyticCategories } from "../../constants/analytics";
import { logger } from "../../logging";
import HelpDeskMessage from "../../help-desk/HelpDeskMessage";

const DocumentLink = ({
  className,
  color,
  pdfUri,
  children,
  filename,
  isPostRequest,
  postPayload,
  disabled,
  timeout = parseInt(window.env.REACT_APP_API_TIMEOUT, 10),
  btnSize,
  inline
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const trackAnalytics = (event, details) => {
    logger.event({
      category: AnalyticCategories.document,
      action: event.target.textContent,
      label: details
    });
  };

  const handleGetPdf = event => {
    event.persist();
    event.stopPropagation();

    const details = `filename: ${filename}, pdfUrl: ${pdfUri}`;
    trackAnalytics(event, details);
    setLoading(true);

    const documentRenderer = new DocumentService();
    documentRenderer.initializeRenderer(LOADING_DOCUMENT_ROUTE);

    //postload.length counts how many forms selected in coverage-forms...
    const key = isPostRequest
      ? "post:" + pdfUri + JSON.stringify(postPayload.length)
      : "get:" + pdfUri;
    sessionStorage.setItem(key, Date.now());

    if (isPostRequest) {
      return fetchPdfs(pdfUri, postPayload).then(
        response => {
          logger.apitimes(key);
          documentRenderer.saveOrOpenDocument(response.data, filename);
          setLoading(false);
        },
        error => {
          documentRenderer.clearRenderer();
          setLoading(false);
          setError(error);
        }
      );
    }

    getPdf(pdfUri, { timeout }).then(
      response => {
        logger.apitimes(key);
        documentRenderer.saveOrOpenDocument(response.data, filename);
        setLoading(false);
      },
      error => {
        logger.error({
          description: error && error.response && error.response.statusText,
          details: `${event.target.textContent} ${details}`
        });
        documentRenderer.clearRenderer();
        setLoading(false);
        setError(error);
      }
    );
  };

  const renderErrorDisplay = () => {
    return (
      <InfoModal
        showModal={true}
        hideModal={() => setError(false)}
        title={Translations.modal.title.pdf_document}
        message={
          <HelpDeskMessage before="There was an error loading your document. For further assistance," />
        }
      />
    );
  };

  if (!pdfUri) return null;

  if (loading) {
    return (
      <LoadingIndicator message={Translations.modal.messages.pdf_loading} />
    );
  }

  return (
    <span className={className}>
      {error && renderErrorDisplay()}
      <Button
        onClick={handleGetPdf}
        className="btn-theme"
        variant={color || "primary"}
        disabled={disabled}
        isLink
        inline={inline || false}
        size={btnSize || "md"}
      >
        {children}
      </Button>
    </span>
  );
};

DocumentLink.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  pdfUri: PropTypes.string.isRequired,
  children: PropTypes.any,
  filename: PropTypes.string.isRequired,
  isPostRequest: PropTypes.bool,
  postPayload: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  disabled: PropTypes.bool,
  timeout: PropTypes.number,
  btnSize: PropTypes.string,
  inline: PropTypes.bool
};

export default DocumentLink;
